import _ from "lodash";

import { determineCellRendererAndParams } from "lib/frontend/cm/utils/custom-column-helpers";
import { PerformanceQuestionWithAnswers } from "lib/frontend/cm/utils/hooks";
import {
  CellExportParams,
  FieldTextSetting,
  HensColDef,
  ProposalColumnFnParams,
} from "lib/frontend/cm/utils/types";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import {
  CustomField,
  FieldType,
  GrantType,
  WeightedComp,
  SalaryIncomeFrequency,
} from "prisma/cm/client";

import { fromBenchmarkPercentile } from "./adapters/from/fromBenchmarkPercentile";
import { fromGrantType } from "./adapters/from/fromGrantType";
import { fromPayPointSettings } from "./adapters/from/fromPayPointSettings";
import { fromPerformanceCycle } from "./adapters/from/fromPerformanceCycle";
import { fromPerformanceQuestion } from "./adapters/from/fromPerformanceQuestion";
import { fromWeightedComp } from "./adapters/from/fromWeightedComp";
import { toAgGridColumnDefinition } from "./adapters/to/toAgGridColumnDefinition";
import { FieldValueResolver } from "./FieldValueResolver";
import { typeMixins } from "./type-mixins";
import {
  DefinitionFieldType,
  FieldCondition,
  FieldComparatorFunc,
  FieldExportFormatterFunc,
  FieldFormatterFunc,
  FieldHydrationContext,
  FieldSourceType,
  FieldValueGetterFunc,
  FieldValueSetterFunc,
  FieldModelType,
  FieldBaseModelMap,
  SystemFieldId,
} from "./types";
import { CompanyAppModule } from "../company/types";
import { annualizeCashValue, roundOffCentsInteger } from "../compensation/cash";
import {
  getInCycleSalaryIncomeFrequency,
  getInCycleEmploymentSubType,
} from "../compensation/pay-frequency";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import {
  getCurrencyOffInstance,
  getSalaryFrequencyOffInstance,
} from "../currency/getCurrencyField";
import { CustomFieldable, getCustomFieldValue, updateCustomFieldValue } from "../custom-fields";
import { PayPointSettings } from "../pay-points/types";
import { PerformanceCycleWithRatings } from "../performance-ratings/types";
export const BEFORE_TEXT = " (Before)";

type AgGridSettings<T> = Partial<Omit<HensColDef<T>, "id" | "cellRendererParams">> & {
  cellExportFormatter?: (params: ProposalColumnFnParams & CellExportParams) => any;
  cellRendererParams?: (params: ProposalColumnFnParams<T>) => any;
};

export enum UsageModel {
  User = "user",
  Proposal = "proposal",
  Range = "range",
}

const DEFAULT_AG_GRID_SETTINGS: AgGridSettings<any> = {
  sortable: true,
  filter: true,
};

export type FieldDefinitionArgs<
  M extends UsageModel = UsageModel,
  T extends FieldModelType = FieldBaseModelMap[M],
  FST extends FieldSourceType = FieldSourceType,
  FID extends SystemFieldId | string = SystemFieldId | string,
> = {
  id: FID;
  baseModel: M;
  valueGetter: FieldValueGetterFunc<T>;
  valueSetter?: FieldValueSetterFunc<T>;
  comparator?: FieldComparatorFunc<T>;
  formatter?: FieldFormatterFunc<T>;
  exportFormatter?: FieldExportFormatterFunc;
  fieldSourceType: FST;
  agGridSettings?: AgGridSettings<T>;
  fieldTypes?: DefinitionFieldType[];

  isRecurringPayment?: boolean;
  annualizedInValueGetter?: boolean;

  includeInDefaultTableSettingsCondition?: FieldCondition[];
  companyAppModuleCondition?: CompanyAppModule[];

  suppressAwardLetterVariable?: boolean;

  input?: boolean;
  isBeforeable?: boolean;
  isNonPromoAfter?: boolean;
  label?: string;
  description?: string;
};

type TextArgs = {
  label?: string;
  description?: string;
};

export class FieldDefinition<
  M extends UsageModel = UsageModel,
  T extends FieldModelType = FieldBaseModelMap[M],
  FST extends FieldSourceType = FieldSourceType,
  FID extends FST extends FieldSourceType.SYSTEM_FIELD
    ? SystemFieldId
    : string = FST extends FieldSourceType.SYSTEM_FIELD ? SystemFieldId : string,
> {
  id: FID;
  baseModel: M;
  valueGetter: FieldValueGetterFunc<T>;
  valueSetter?: FieldValueSetterFunc<T>;
  formatter?: FieldFormatterFunc<T>;
  exportFormatter?: FieldExportFormatterFunc;
  agGridSettings?: AgGridSettings<T>;
  fieldSourceType: FST;
  fieldTypes: DefinitionFieldType[] = [];
  comparator?: FieldComparatorFunc<T>;
  includeInDefaultTableSettingsCondition?: FieldCondition[];
  companyAppModuleCondition?: CompanyAppModule[];

  isRecurringPayment: boolean;
  annualizedInValueGetter?: boolean;

  label?: string;
  description?: string;
  textPopulated: boolean = false;

  suppressAwardLetterVariable: boolean;

  isInput: boolean = false;
  isComposite: boolean = false;
  isBeforeable: boolean = false;

  // for after values that aren't promotion related, we want to show null instead of the before until the real after value is present.
  // most value getters factor this in but the composite fields need to know this to show the original value
  isNonPromoAfter: boolean = false;

  originalField?: FieldDefinition;

  constructor(args: FieldDefinitionArgs<M, T, FST, FID> & TextArgs, applyMixins = true) {
    this.label = args.label;
    this.description = args.description;

    this.fieldTypes = args.fieldTypes ?? [];

    this.id = args.id;
    this.baseModel = args.baseModel;
    this.valueGetter = args.valueGetter;
    this.valueSetter = args.valueSetter;
    this.fieldSourceType = args.fieldSourceType;
    this.agGridSettings = Object.assign({}, DEFAULT_AG_GRID_SETTINGS, args.agGridSettings);
    this.comparator = args.comparator;
    this.includeInDefaultTableSettingsCondition = args.includeInDefaultTableSettingsCondition;
    this.companyAppModuleCondition = args.companyAppModuleCondition;

    this.isRecurringPayment = args.isRecurringPayment ?? false;
    this.annualizedInValueGetter = args.annualizedInValueGetter ?? false;

    this.isInput = args.input ?? false;
    this.isBeforeable = args.isBeforeable ?? false;
    this.isNonPromoAfter = args.isNonPromoAfter ?? false;

    this.suppressAwardLetterVariable = args.suppressAwardLetterVariable ?? false;

    if (args.label) {
      this.label = args.label;
      this.textPopulated = true;

      if (args.description) {
        this.description = args.description;
      }
    }

    if (args.formatter) {
      this.formatter = args.formatter;
    }

    if (args.exportFormatter) {
      this.exportFormatter = args.exportFormatter;
    }

    if (applyMixins) {
      this.fieldTypes.forEach((fieldType) => {
        const mixin = typeMixins[fieldType];
        const mixinWithoutExplicitlySetValues = Object.keys(mixin).reduce(
          (acc, key) => {
            if (
              this[key as keyof this] === undefined &&
              acc[key as keyof typeof acc] === undefined
            ) {
              // @ts-expect-error TS7053
              acc[key] = mixin[key];
            }
            return acc;
          },
          {} as typeof mixin,
        );

        mixinWithoutExplicitlySetValues.agGridSettings = _.merge(
          {},
          this.agGridSettings,
          mixin.agGridSettings,
        );

        Object.assign(this, mixinWithoutExplicitlySetValues);

        if (_.isNil(this.formatter)) {
          this.formatter = (args) => {
            return args.value;
          };
        }
      });
    }
  }

  public populateText(fieldTextSettings: FieldTextSetting[], proposalUsageContext: boolean): void {
    const fieldTextSetting = fieldTextSettings.find((setting) => setting.fieldId === this.id);
    if (!fieldTextSetting) {
      if (this.label) {
        return;
      }

      throw new Error(`Field text setting not found for field ${this.id}`);
    }

    if (this.isBeforeable && proposalUsageContext) {
      this.label = fieldTextSetting.label + BEFORE_TEXT;
    } else {
      this.label = fieldTextSetting.label;
    }
    this.description = fieldTextSetting.description;
    this.textPopulated = true;
  }

  public clone() {
    return new FieldDefinition(this);
  }

  public hydrate(ctx: FieldHydrationContext): HydratedFieldDefinition<M, T> {
    return new HydratedFieldDefinition(this, ctx);
  }

  static extend<M extends UsageModel, T extends FieldModelType>(
    fieldDefinition: FieldDefinition,
    args: FieldDefinitionArgs<M, T>,
  ): FieldDefinition<M, T> {
    // @ts-expect-error TS2345
    const newDef = new FieldDefinition({ ...fieldDefinition, ...args }, false);
    newDef.originalField = fieldDefinition;
    return newDef;
  }

  static fromCustomField(
    customFieldDef: CustomField,
    modelContext: "Range" | "Proposal" | "User",
  ): FieldDefinition {
    let baseModel = UsageModel.Proposal;
    switch (customFieldDef.model) {
      case "User":
        baseModel = UsageModel.User;
        break;
      case "Proposal":
        baseModel = UsageModel.Proposal;
        break;
      case "Range":
        baseModel = UsageModel.Range;
        break;
      default:
        throw new Error(`Unknown model type: ${customFieldDef.model}`);
    }

    const def = new FieldDefinition({
      id: customFieldDef.id,
      baseModel: baseModel,
      input: customFieldDef.isEditable,
      fieldSourceType: FieldSourceType.CUSTOM_FIELD,
      isRecurringPayment: customFieldDef.isRecurringPayment,
      valueGetter: (instance) => {
        return getCustomFieldValue(instance as CustomFieldable, customFieldDef.id);
      },
      valueSetter: ({ instance, newValue }) => {
        if (!customFieldDef.isEditable) return false;

        if (_.isNil(newValue) || newValue === "") {
          updateCustomFieldValue({
            customFieldable: instance as CustomFieldable,
            fieldId: customFieldDef.id,
            newValue: null,
            manuallyEntered: true,
          });

          return true;
        } else {
          let finalValue = newValue;
          if (customFieldDef.valueType === FieldType.PERCENT) {
            finalValue = Number(newValue) / 100;
          }

          updateCustomFieldValue({
            customFieldable: instance as CustomFieldable,
            fieldId: customFieldDef.id,
            newValue: finalValue,
            manuallyEntered: true,
          });

          return true;
        }
      },
      fieldTypes: [customFieldDef.valueType],
      agGridSettings: {
        ...determineCellRendererAndParams(customFieldDef, modelContext),
      },
    });

    def.label = customFieldDef.label;
    def.description = customFieldDef.description ?? undefined;
    def.textPopulated = true;

    return def;
  }

  static fromPerformanceQuestion(
    performanceQuestion: PerformanceQuestionWithAnswers,
  ): FieldDefinition {
    return fromPerformanceQuestion(performanceQuestion);
  }

  static fromPerformanceCycle(performanceCycle: PerformanceCycleWithRatings): FieldDefinition {
    return fromPerformanceCycle(performanceCycle);
  }

  static fromPayPointSettings(payPointSettings: PayPointSettings): FieldDefinition[] {
    return fromPayPointSettings(payPointSettings);
  }

  static fromGrantType(grantType: GrantType): FieldDefinition[] {
    return fromGrantType(grantType);
  }

  static fromBenchmarkPercentile(targetPercentile: number): FieldDefinition {
    return fromBenchmarkPercentile(targetPercentile);
  }

  static fromWeightedComp(weightedComp: WeightedComp): FieldDefinition {
    return fromWeightedComp(weightedComp);
  }
}

export class SystemFieldDefinition<
  M extends UsageModel = UsageModel,
  T extends FieldModelType = FieldBaseModelMap[M],
> extends FieldDefinition {
  constructor(
    args: Omit<
      FieldDefinitionArgs<M, T, FieldSourceType.SYSTEM_FIELD, SystemFieldId>,
      "fieldSourceType"
    > & {
      fieldSourceType?: never;
    },
  ) {
    // isaac: cannot for the life of me get this to work
    // @ts-expect-error TS2345
    super({
      ...args,
      fieldSourceType: FieldSourceType.SYSTEM_FIELD,
    });
  }
}

export class HydratedFieldDefinition<
  M extends UsageModel = UsageModel,
  T extends FieldModelType = FieldBaseModelMap[M],
> extends FieldDefinition<M, T> {
  context: FieldHydrationContext;

  constructor(args: FieldDefinition<M, T> & TextArgs, context: FieldHydrationContext) {
    super(args, false);
    this.context = context;
  }

  getValue(instance: T): any {
    try {
      const resolver = new FieldValueResolver({
        // @ts-expect-error TS2322
        fieldDefinition: this,
        modelInstance: instance,
        context: this.context,
      });

      return resolver.getValue();
    } catch (e: any) {
      const newError = new Error(`Error in getValue with colId ${this.id}: ${e.message}`);
      newError.stack = e.stack;
      throw newError;
    }
  }

  getFormattedValue(instance: T): string {
    try {
      const resolver = new FieldValueResolver({
        // @ts-expect-error TS2322
        fieldDefinition: this,
        modelInstance: instance,
        context: this.context,
      });

      return resolver.getFormattedValue();
    } catch (e: any) {
      const newError = new Error(`Error in getFormattedValue with colId ${this.id}: ${e.message}`);
      newError.stack = e.stack;
      throw newError;
    }
  }

  /**
   * @throws Error if the field is not a DOLLAR field
   */
  getValueInTargetCurrency(
    instance: T,
    options: {
      roundOffCents?: boolean;
      annualize?: boolean;
    } = {},
  ): number {
    const { roundOffCents = false, annualize = false } = options;

    // This method should only be used with DOLLAR fields
    if (!this.fieldTypes?.includes("DOLLAR")) {
      throw new Error(
        `getValueInTargetCurrency can only be used with DOLLAR fields. Field "${this.id}" has types: ${this.fieldTypes?.join(", ") || "none"}`,
      );
    }

    let rawValue = this.getValue(instance);

    if (!this.context.targetCurrency || !this.context.exchangeRates) {
      throw new Error(
        `getValueInTargetCurrency requires targetCurrency and exchangeRates in the context. Field "${this.id}"`,
      );
    }

    if (annualize && this.isRecurringPayment && !this.annualizedInValueGetter) {
      let incomeFrequency: SalaryIncomeFrequency | null | undefined = "YEARLY";
      let employmentSubType: EmploymentSubType | null | undefined = "full_time";
      if (FieldValueResolver.validateAsProposal(instance)) {
        const proposal = instance;
        incomeFrequency = getInCycleSalaryIncomeFrequency({ proposalWithOwner: proposal });
        employmentSubType = getInCycleEmploymentSubType({ proposalWithOwner: proposal });
      } else {
        incomeFrequency = getSalaryFrequencyOffInstance(instance);
        employmentSubType = FieldValueResolver.validateAsUser(instance)
          ? (instance.employmentSubType as EmploymentSubType)
          : "full_time";
      }

      rawValue = annualizeCashValue({
        value: rawValue,
        incomeFrequency,
        employmentSubType,
      });
    }

    const sourceCurrency = getCurrencyOffInstance(instance);
    const converted = getConvertedCurrency({
      value: rawValue,
      options: {
        sourceCurrency: sourceCurrency,
        targetCurrency: this.context.targetCurrency,
        exchangeRates: this.context.exchangeRates,
      },
    }).raw;

    if (_.isNaN(converted)) {
      // todo isaac: its not clear to me why this is happening, i think it has to do with annualization but this unblocks for now
      return 0;
    }

    // Round off cents if requested: rounds to nearest dollar in cents (e.g., 12750 -> 12700)
    if (roundOffCents) {
      return roundOffCentsInteger(converted);
    } else {
      return converted;
    }
  }

  toAgGridColumnDefinition(): HensColDef {
    return toAgGridColumnDefinition(this);
  }
}
